import React from 'react'
import { useAppDispatch, useAppSelector } from '../../hooks/redux'

import { arraysEqual } from '../../utilities'
import { postStack, removeFromStack } from '../../lib/api'
import { resetExtra } from '../../slices/chatControlsSlice'
import { useSocket } from '../SocketProvider'
import { selectDMs, setSelected as setSelectedAction } from '../../slices/dms'
import { selectConvo, setLastSelected } from '../../slices/convo'
import { useReadStatus } from '../ReadStatusProvider'

type QueueStateContextType = {
  id: string
  conversation: LocalSession | DM
  conversations: Record<string, LocalSession | DM>
  handleSelect: (id: string, cb?: () => void) => void
  handleClose: (id: string, cb?: () => void) => void
  order: string[]
  setOrder: React.Dispatch<React.SetStateAction<string[]>>
  resetSelected: () => void
  updateQueue: (id: string, type: 'session' | 'user') => Promise<void>
  setAccepted: React.Dispatch<React.SetStateAction<boolean>>
  accepted: boolean
  scrollTo: string
  setScrollTo: React.Dispatch<React.SetStateAction<string>>
}

const QueueStateContext = React.createContext<QueueStateContextType>(null)

const QueueStateProvider = ({ isPeerChat = false, children }) => {
  const dispatch = useAppDispatch()

  const {
    api: { markSeen },
    peerChat: { setNewPush }
  } = useSocket()
  const { dms } = useAppSelector(selectDMs)
  const { allConversations } = useAppSelector(selectConvo)
  const { setWholeConvos } = useReadStatus()

  const [accepted, setAccepted] = React.useState<boolean>(false)
  const [scrollTo, setScrollTo] = React.useState<string>(null)
  const [selected, setSelected] = React.useState('')
  const [order, _setOrder] = React.useState<string[]>([])

  const convos = React.useMemo(() => {
    if (isPeerChat) {
      return dms
    }

    return allConversations
  }, [isPeerChat, dms, allConversations])

  const selectedConversation = React.useMemo(() => {
    if (!selected?.length) {
      return null
    }

    if (!(selected in convos)) {
      return null
    }

    return convos[selected]
  }, [selected, convos])

  const clearUnread = React.useCallback(
    id => {
      setWholeConvos(convs => ({
        ...convs,
        [id]: { unread: 0, cleared: true }
      }))
    },
    [setWholeConvos]
  )

  const handleSelect: QueueStateContextType['handleSelect'] = React.useCallback(
    (id, cb) => {
      setSelected(id)

      if (!isPeerChat) {
        dispatch(setLastSelected(id))
      }

      // marking convo as seen and clearing it from count
      markSeen(id, false)
      clearUnread(id)
      // saving to provider for notifs reference
      const emptyTimeline = sessionStorage.getItem('empty')
      if (!emptyTimeline) {
        dispatch(setSelectedAction(id))
        setNewPush(old => old.filter(session => session !== id))
      }
      cb && cb()
    },
    [dispatch, setNewPush, clearUnread, markSeen, isPeerChat]
  )

  const setOrder = React.useCallback(newOrder => {
    _setOrder(oldOrder => {
      if (arraysEqual(oldOrder, newOrder, true)) {
        return oldOrder
      }

      return newOrder
    })
  }, [])

  // updating peer chat queue
  const updateQueue: QueueStateContextType['updateQueue'] = React.useCallback(
    async (id, type) => {
      try {
        await postStack({ id, type }, 'default')
        setScrollTo(id)
      } catch (err) {
        console.error('Error posting stack', err)
      }
    },
    []
  )

  const removeConvo = React.useCallback(async id => {
    try {
      await removeFromStack(id, 'default')
    } catch (err) {
      console.error('Error removing convo', err)
    }
  }, [])

  const handleClose = React.useCallback(
    (id, cb) => {
      const onFinish = () => {
        setOrder(order.filter(oID => oID !== id))
        removeConvo(id)
        cb && cb()
      }

      // Do nothing if current wasn't closed
      if (id !== selected) {
        return onFinish()
      }

      // Clear selection if last convo closed
      if (order.length === 1) {
        setSelected('')
        return onFinish()
      }

      // Select second last if on last
      if (selected === order[order.length - 1]) {
        setSelected(order[order.length - 2])
        return onFinish()
      }

      // Select next in general
      setSelected(order[order.findIndex(oID => oID === id) + 1])
      return onFinish()
    },
    [setOrder, selected, order, removeConvo]
  )

  const resetSelected: QueueStateContextType['resetSelected'] =
    React.useCallback(() => {
      setSelected('')
    }, [])

  React.useEffect(() => {
    dispatch(resetExtra())
  }, [selected, dispatch])

  return (
    <QueueStateContext.Provider
      value={{
        id: selected,
        conversation: selectedConversation,
        conversations: convos,
        handleSelect,
        handleClose,
        order,
        setOrder,
        resetSelected,
        updateQueue,
        setAccepted,
        accepted,
        scrollTo,
        setScrollTo
      }}
    >
      {children}
    </QueueStateContext.Provider>
  )
}

export default QueueStateProvider

export const useQueueState = () => React.useContext(QueueStateContext)
